import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarDays, Users, Heart, Ticket, ChevronRight, Camera, Trash2, KeyRound, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DeleteAccountModal } from "@/components/DeleteAccountModal";
import { PublicEventModal } from "@/components/PublicEventModal";
import { statusMetaOf } from "@/components/admin/AdminEvents";
import { SpotCard } from "@/components/SpotCard";
import { EventCard } from "@/components/EventCard";
import { useLanguage } from "@/i18n/LanguageContext";

export default function ProfilPage() {
  const { user, favorites, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { t, lang, fmtDate } = useLanguage();
  const [reservations, setReservations] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [myEvents, setMyEvents] = useState(null);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);

  const loadReservations = () =>
    api.get("/reservations").then((r) => setReservations(r.data)).catch(() => setReservations([]));

  const loadMyEvents = () =>
    api.get("/events/mine").then((r) => setMyEvents(r.data)).catch(() => setMyEvents([]));

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/avatar", fd);
      await refreshUser();
      toast.success(t("Photo de profil mise à jour !"));
    } catch (err) {
      toast.error(t(formatApiError(err.response?.data?.detail)));
    } finally {
      setUploading(false);
    }
  };

  const cancelReservation = async (r) => {
    const msg = lang === "en" ? `Cancel your reservation for "${r.event_title}"?` : `Annuler ta réservation pour « ${r.event_title} » ?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/reservations/${r.id}`);
      toast.success(t("Réservation annulée — courriel de confirmation envoyé"));
      loadReservations();
    } catch (err) {
      toast.error(t(formatApiError(err.response?.data?.detail)));
    }
  };

  const openCreateEvent = () => {
    setEditingEvent(null);
    setEventModalOpen(true);
  };

  const openEditEvent = (ev) => {
    setEditingEvent(ev);
    setEventModalOpen(true);
  };

  const removeMyEvent = async (ev) => {
    if (!window.confirm(t("Supprimer cet événement ?"))) return;
    try {
      await api.delete(`/events/${ev.slug}`);
      toast.success(t("Événement supprimé"));
      loadMyEvents();
    } catch (err) {
      toast.error(t(formatApiError(err.response?.data?.detail)));
    }
  };

  useEffect(() => {
    if (user === false) navigate("/connexion");
  }, [user, navigate]);

  useEffect(() => {
    if (user) {
      loadReservations();
      loadMyEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-24">
        <div className="h-64 animate-pulse rounded-3xl bg-white" />
      </main>
    );
  }

  const favEntries = Object.entries(favorites);

  return (
    <main data-testid="profil-page" className="mx-auto max-w-5xl px-4 pb-16 pt-10 sm:px-6">
      <Seo title={t("Mon profil — GoSpot")} siteName="GoSpot" description={t("Tes réservations et tes favoris GoSpot.")} />

      <div className="flex items-center gap-4" data-testid="profil-header">
        <div className="relative">
          {user.avatar ? (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${user.avatar}`}
              alt={user.name}
              data-testid="profil-avatar-image"
              className="h-16 w-16 rounded-3xl object-cover"
            />
          ) : (
            <span
              data-testid="profil-avatar-initial"
              className="flex h-16 w-16 items-center justify-center rounded-3xl bg-[#2563EB] text-2xl font-extrabold text-white"
            >
              {user.name?.[0]?.toUpperCase() || "U"}
            </span>
          )}
          <button
            type="button"
            data-testid="avatar-upload-button"
            onClick={() => fileInput.current?.click()}
            aria-label={t("Changer ma photo")}
            className="absolute -bottom-1.5 -right-1.5 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-[#2563EB] text-white shadow transition hover:bg-[#1D4ED8]"
          >
            <Camera className="h-4 w-4" />
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            data-testid="avatar-file-input"
            className="hidden"
            onChange={uploadAvatar}
          />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{user.name}</h1>
          <p className="text-sm text-slate-500">{user.email}</p>
        </div>
      </div>

      {/* RÉSERVATIONS */}
      <section className="mt-10" data-testid="profil-reservations">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
          <Ticket className="h-5 w-5 text-[#2563EB]" />
          {t("Mes réservations")}
        </h2>
        {reservations === null ? (
          <div className="mt-4 h-24 animate-pulse rounded-3xl bg-white" />
        ) : reservations.length === 0 ? (
          <div className="mt-4 rounded-3xl border border-slate-100 bg-white p-8 text-center">
            <p className="text-sm text-slate-500">{t("Aucune réservation pour l'instant.")}</p>
            <Link to="/evenements" data-testid="profil-explore-events" className="mt-4 inline-block rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#1D4ED8]">
              {t("Explorer les événements")}
            </Link>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {reservations.map((r) => (
              <div
                key={r.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/evenement/${r.event_slug}`)}
                data-testid={`reservation-${r.event_slug}`}
                className="flex cursor-pointer items-center gap-4 rounded-2xl border border-slate-100 bg-white p-3 shadow-[0_4px_16px_rgba(15,23,42,0.05)] transition duration-300 hover:-translate-y-0.5 hover:border-blue-200"
              >
                {r.event?.image && (
                  <img src={r.event.image} alt={r.event_title} className="h-16 w-16 shrink-0 rounded-xl object-cover" />
                )}
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-sm font-semibold text-slate-900">{r.event_title}</h3>
                  <p className="truncate text-xs text-slate-500">{r.event?.venue}</p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <CalendarDays className="h-3 w-3" />
                      {fmtDate(r.event_date)} {r.event?.time_label && `• ${r.event.time_label}`}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {r.guests} {lang === "en" ? (r.guests > 1 ? "people" : "person") : `personne${r.guests > 1 ? "s" : ""}`}
                    </span>
                  </p>
                </div>
                <span className="hidden shrink-0 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-600 sm:inline">
                  {t("Confirmée")}
                </span>
                <button
                  type="button"
                  data-testid={`cancel-reservation-${r.event_slug}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    cancelReservation(r);
                  }}
                  className="shrink-0 rounded-full border border-slate-200 px-3.5 py-1.5 text-xs font-semibold text-slate-500 transition hover:border-red-300 hover:text-red-500"
                >
                  {t("Annuler")}
                </button>
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* MES ÉVÉNEMENTS */}
      <section className="mt-10" data-testid="profil-mes-evenements">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
            <CalendarDays className="h-5 w-5 text-[#2563EB]" />
            {t("Mes événements")}
          </h2>
          <button
            type="button"
            data-testid="profil-publish-event-button"
            onClick={openCreateEvent}
            className="flex items-center gap-2 rounded-full bg-[#2563EB] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#1D4ED8] active:scale-95"
          >
            <Plus className="h-3.5 w-3.5" />
            {t("Publier un événement")}
          </button>
        </div>
        {myEvents === null ? (
          <div className="mt-4 h-24 animate-pulse rounded-3xl bg-white" />
        ) : myEvents.length === 0 ? (
          <div className="mt-4 rounded-3xl border border-slate-100 bg-white p-8 text-center">
            <p className="text-sm text-slate-500">{t("Aucun événement soumis pour l'instant.")}</p>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {myEvents.map((ev) => {
              const st = statusMetaOf(ev);
              return (
                <div key={ev.slug} data-testid={`my-event-${ev.slug}`} className="rounded-2xl border border-slate-100 bg-white p-3 shadow-[0_4px_16px_rgba(15,23,42,0.05)]">
                  <div className="flex flex-wrap items-center gap-3 sm:flex-nowrap sm:gap-4">
                    {ev.image && (
                      <img src={ev.image} alt={ev.title} className="h-16 w-16 shrink-0 rounded-xl object-cover" />
                    )}
                    <div className="min-w-0 flex-1">
                      <h3 className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
                        <span className="truncate">{ev.title}</span>
                        <span data-testid={`my-event-status-${ev.slug}`} className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${st.cls}`}>{t(st.label)}</span>
                      </h3>
                      <p className="mt-0.5 truncate text-xs text-slate-500">{ev.venue}{ev.city ? `, ${ev.city}` : ""}</p>
                      <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-400">
                        <CalendarDays className="h-3 w-3" />
                        {fmtDate(ev.date)} {ev.time_label && `• ${ev.time_label}`}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        data-testid={`edit-my-event-${ev.slug}`}
                        onClick={() => openEditEvent(ev)}
                        className="flex items-center gap-1.5 rounded-full border border-slate-200 px-3.5 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-blue-300 hover:text-[#2563EB]"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        {t("Modifier")}
                      </button>
                      <button
                        type="button"
                        data-testid={`delete-my-event-${ev.slug}`}
                        onClick={() => removeMyEvent(ev)}
                        className="flex items-center gap-1.5 rounded-full border border-slate-200 px-3.5 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-red-300 hover:text-red-500"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {t("Supprimer")}
                      </button>
                    </div>
                  </div>
                  {ev.status === "rejected" && ev.rejection_reason && (
                    <p data-testid={`my-event-reason-${ev.slug}`} className="mt-2 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-600">
                      <strong>{t("Raison du refus")} :</strong> {ev.rejection_reason}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* FAVORIS */}
      <section className="mt-10" data-testid="profil-favoris">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
          <Heart className="h-5 w-5 text-red-500" />
          {t("Mes favoris")}
        </h2>
        {favEntries.length === 0 ? (
          <p className="mt-4 rounded-3xl border border-slate-100 bg-white p-8 text-center text-sm text-slate-500">
            {t("Aucun favori pour l'instant. Touche le cœur sur un spot ou un événement pour l'ajouter ici.")}
          </p>
        ) : (
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {favEntries.map(([key, item]) =>
              key.startsWith("etablissement:") ? (
                <SpotCard key={key} spot={item} />
              ) : (
                <EventCard key={key} event={item} />
              )
            )}
          </div>
        )}
      </section>

      {/* COMPTE */}
      <section className="mt-10" data-testid="profil-compte">
        <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
          <KeyRound className="h-5 w-5 text-slate-400" />
          {t("Compte")}
        </h2>
        <div className="mt-4 flex flex-col gap-4 rounded-3xl border border-red-100 bg-white p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-900">{t("Supprimer mon compte")}</p>
            <p className="mt-1 text-xs text-slate-500">
              {t("Action définitive : tes réservations, favoris et données personnelles seront effacés. Tes avis seront anonymisés.")}
            </p>
          </div>
          <button
            type="button"
            data-testid="delete-account-button"
            onClick={() => setDeleteOpen(true)}
            className="flex shrink-0 items-center justify-center gap-2 rounded-full border border-red-200 px-5 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
            {t("Supprimer mon compte")}
          </button>
        </div>
      </section>

      <DeleteAccountModal open={deleteOpen} onClose={() => setDeleteOpen(false)} />
      <PublicEventModal open={eventModalOpen} onClose={() => setEventModalOpen(false)} event={editingEvent} onSaved={loadMyEvents} />
    </main>
  );
}
